import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useCollectionBinding,
  useProperty,
  usePropertyField,
  useSlotGroup,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';

/**
 * Weidner Via6 HomeMap section.
 *
 * Mirrors via6living.com's "So much to do in the neighborhood." block —
 * the neighborhood-context map section below the bottom divider on Home.
 *
 *   ┌────────────────────────────────────────────────────────────┐
 *   │  ┌──────────────────────┐  ┌──────────────────────────┐    │
 *   │  │ So much to do in the │  │                          │    │
 *   │  │ neighborhood.        │  │   [Google Map w/ POIs]   │    │
 *   │  │                      │  │                          │    │
 *   │  │ Walk Score 99 …      │  │   • property pin         │    │
 *   │  │ All this comes …     │  │   • category POI pins    │    │
 *   │  │ [View Neighborhood]  │  │                          │    │
 *   │  └──────────────────────┘  └──────────────────────────┘    │
 *   └────────────────────────────────────────────────────────────┘
 *
 * The map is a small read-only preview of the full Neighborhood page:
 * same property pin, same POI markers from the `neighborhood_pois`
 * collection, same brand-color base styling — but with no tab bar,
 * no filter dropdown, no POI list, and no info windows. The "View
 * Neighborhood" CTA points at the full page for those.
 *
 * Editor surface:
 *   - `homemap.heading` — section title.
 *   - `homemap.body` — the richtext body paragraph.
 *   - `homemap.pois` (collection) — POIs to plot, shared with the full
 *     NeighborhoodMap section via the `neighborhood_pois` collection.
 *   - `homemap.cta` (link slot group) — "View Neighborhood" CTA.
 *
 * Colors are pulled from theme tokens via CSS custom properties so the
 * heading/body inherit the host theme's heading font (Prata on Via6) and
 * the CTA button uses `--vibe-color-secondary` from tokens.ts.
 */

// ============================================================================
// Types
// ============================================================================

interface POI {
  poi_name?: string;
  poi_type?: string;
  poi_address?: string;
  poi_latitude?: string;
  poi_longitude?: string;
}

// ============================================================================
// Google Maps loader + marker SVGs (mirrors NeighborhoodMap styling)
// ============================================================================

const MAPS_API_KEY =
  (typeof import.meta !== 'undefined' &&
    (import.meta as { env?: Record<string, string> }).env?.GOOGLE_MAPS_API_KEY) ||
  '';

let mapsLoaderPromise: Promise<void> | null = null;

function loadGoogleMaps(): Promise<void> {
  if ((window as any).google?.maps?.Map) return Promise.resolve();
  if (mapsLoaderPromise) return mapsLoaderPromise;
  mapsLoaderPromise = new Promise<void>((resolve, reject) => {
    if (!MAPS_API_KEY) {
      reject(new Error('No key'));
      return;
    }
    const cb = `__gmapsInit_${Date.now()}`;
    (window as any)[cb] = () => {
      delete (window as any)[cb];
      resolve();
    };
    const s = document.createElement('script');
    s.src = `https://maps.googleapis.com/maps/api/js?key=${MAPS_API_KEY}&callback=${cb}`;
    s.onerror = () => {
      mapsLoaderPromise = null;
      reject(new Error('Script failed'));
    };
    document.head.appendChild(s);
  });
  return mapsLoaderPromise;
}

function makeSvgMarker(fillColor: string, glyphSvg: string, size = 28): string {
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${Math.round(
    size * 1.3,
  )}" viewBox="0 0 30 39">
  <path d="M15 0C6.72 0 0 6.72 0 15c0 11.25 15 24 15 24s15-12.75 15-24C30 6.72 23.28 0 15 0z" fill="${fillColor}" stroke="#fff" stroke-width="1.5"/>
  <g transform="translate(7,7)" fill="#fff"><g transform="translate(8,8) scale(0.55) translate(-8,-8)">${glyphSvg}</g></g>
</svg>`)}`;
}

function makePropertyPinBg(primaryColor: string): string {
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" width="52" height="64" viewBox="0 0 60 74">
  <ellipse cx="30" cy="70" rx="12" ry="4" fill="rgba(0,0,0,0.2)"/>
  <path d="M30 0C13.43 0 0 13.43 0 30c0 22.5 30 42 30 42s30-19.5 30-42C60 13.43 46.57 0 30 0z" fill="${primaryColor}" stroke="#fff" stroke-width="2.5"/>
  <circle cx="30" cy="27" r="18" fill="#fff" opacity="0.95"/>
  <path d="M24 26v10h4v-5h4v5h4V26l-6-5z" fill="${primaryColor}"/>
  <path d="M21 27l9-8 9 8" fill="none" stroke="${primaryColor}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`)}`;
}

const CATEGORY_ICONS: Record<string, { glyph: string }> = {
  restaurant: {
    glyph:
      '<path d="M2 0v6h2v10h2V6h2V0H6V4H4V0zm8 0v8h4V6h2v10h2V0z" transform="scale(0.9)"/>',
  },
  shopping: {
    glyph:
      '<path d="M2 2v2H0v10h16V4h-2V2a4 4 0 00-8 0v0zm2 0a2 2 0 014 0v2H4V2z" transform="scale(0.95)"/>',
  },
  education: { glyph: '<path d="M8 0L0 4l8 4 8-4-8-4zM2 6v4l6 3 6-3V6L8 9 2 6z"/>' },
  health: {
    glyph: '<path d="M6 0v6H0v4h6v6h4v-6h6V6h-6V0z" transform="scale(0.95)"/>',
  },
  fitness: {
    glyph: '<path d="M6 0v6H0v4h6v6h4v-6h6V6h-6V0z" transform="scale(0.95)"/>',
  },
  entertainment: {
    glyph:
      '<path d="M8 0a8 8 0 100 16A8 8 0 008 0zm0 2a6 6 0 110 12A6 6 0 018 2zm-2 3v6l5-3-5-3z" transform="scale(0.95)"/>',
  },
  park: {
    glyph:
      '<path d="M8 0L4 6h2L2 12h12L10 6h2L8 0zM7 12v4h2v-4z" transform="scale(0.95)"/>',
  },
  transit: {
    glyph:
      '<path d="M4 0C2 0 0 1 0 3v7a3 3 0 003 3l-1 2v1h2l1-2h6l1 2h2v-1l-1-2a3 3 0 003-3V3c0-2-2-3-4-3zm0 2h8v4H4V2zm0 7a1.5 1.5 0 110 3 1.5 1.5 0 010-3zm8 0a1.5 1.5 0 110 3 1.5 1.5 0 010-3z" transform="scale(0.95)"/>',
  },
  grocery: {
    glyph:
      '<path d="M0 0v2h2l3 6.5L3.5 11A2 2 0 005.5 13h9v-2h-9l1-2h6a1 1 0 00.9-.5L15.5 4H4L3 2H0zm5.5 14a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm7 0a1.5 1.5 0 100 3 1.5 1.5 0 000-3z" transform="scale(0.95)"/>',
  },
  default: { glyph: '<circle cx="8" cy="8" r="4" fill="#fff"/>' },
};

function getCategoryGlyph(poiType: string): string {
  const lower = (poiType || '').toLowerCase();
  for (const [key, val] of Object.entries(CATEGORY_ICONS)) {
    if (key === 'default') continue;
    if (lower.includes(key)) return val.glyph;
  }
  return CATEGORY_ICONS.default.glyph;
}

function hexToHue(hex: string): number {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!m) return 200;
  const r = parseInt(m[1], 16) / 255,
    g = parseInt(m[2], 16) / 255,
    b = parseInt(m[3], 16) / 255;
  const max = Math.max(r, g, b),
    min = Math.min(r, g, b);
  if (max === min) return 0;
  const d = max - min;
  let h = 0;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;
  return Math.round(h * 360);
}

function hueToHex(hue: number, sat = 45, light = 35): string {
  const h = hue / 360,
    s = sat / 100,
    l = light / 100;
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const r = Math.round(hue2rgb(p, q, h + 1 / 3) * 255);
  const g = Math.round(hue2rgb(p, q, h) * 255);
  const b = Math.round(hue2rgb(p, q, h - 1 / 3) * 255);
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

function buildMapStyles(primaryColor: string) {
  const hue = hexToHue(primaryColor);
  return [
    { elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
    { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
    { elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
    { elementType: 'labels.text.stroke', stylers: [{ color: '#f5f5f5' }] },
    {
      featureType: 'administrative.land_parcel',
      elementType: 'labels.text.fill',
      stylers: [{ color: '#bdbdbd' }],
    },
    { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#eeeeee' }] },
    { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
    { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#e5e5e5' }] },
    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
    {
      featureType: 'road.arterial',
      elementType: 'labels.text.fill',
      stylers: [{ color: '#757575' }],
    },
    { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#dadada' }] },
    {
      featureType: 'road.highway',
      elementType: 'labels.text.fill',
      stylers: [{ color: '#616161' }],
    },
    { featureType: 'transit.line', elementType: 'geometry', stylers: [{ color: '#e5e5e5' }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: hueToHex(hue, 15, 85) }] },
    { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
  ];
}

// ============================================================================
// Map preview (no tabs, no list, no info windows)
// ============================================================================

function MapPreview({
  propertyLat,
  propertyLng,
  primaryColor,
  pois,
}: {
  propertyLat: number;
  propertyLng: number;
  primaryColor: string;
  pois: POI[];
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);

  // Init map once.
  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps()
      .then(() => {
        if (cancelled || !containerRef.current) return;
        const g = (window as any).google.maps;
        const map = new g.Map(containerRef.current, {
          center: { lat: propertyLat, lng: propertyLng },
          zoom: 15,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          zoomControl: false,
          gestureHandling: 'cooperative',
          keyboardShortcuts: false,
          clickableIcons: false,
          styles: buildMapStyles(primaryColor),
        });

        new g.Marker({
          position: { lat: propertyLat, lng: propertyLng },
          map,
          title: 'Property',
          zIndex: 999,
          icon: {
            url: makePropertyPinBg(primaryColor),
            scaledSize: new g.Size(52, 64),
            anchor: new g.Point(26, 64),
          },
        });

        mapRef.current = map;
        setReady(true);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [propertyLat, propertyLng, primaryColor]);

  // Sync POI markers when pois change.
  useEffect(() => {
    const map = mapRef.current;
    const g = (window as any).google?.maps;
    if (!map || !g || !ready) return;

    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];

    const bounds = new g.LatLngBounds();
    bounds.extend({ lat: propertyLat, lng: propertyLng });
    let hasPoiBounds = false;

    for (const poi of pois) {
      const lat = parseFloat(poi.poi_latitude || '');
      const lng = parseFloat(poi.poi_longitude || '');
      if (isNaN(lat) || isNaN(lng)) continue;

      const pos = { lat, lng };
      bounds.extend(pos);
      hasPoiBounds = true;

      const glyph = getCategoryGlyph(poi.poi_type || '');
      const marker = new g.Marker({
        position: pos,
        map,
        title: poi.poi_name || '',
        clickable: false,
        icon: {
          url: makeSvgMarker('#888', glyph, 26),
          scaledSize: new g.Size(26, 34),
          anchor: new g.Point(13, 34),
        },
      });
      markersRef.current.push(marker);
    }

    if (hasPoiBounds) {
      map.fitBounds(bounds, { top: 40, right: 40, bottom: 40, left: 40 });
      const listener = g.event.addListener(map, 'idle', () => {
        if (map.getZoom() > 15) map.setZoom(15);
        g.event.removeListener(listener);
      });
    }
  }, [pois, propertyLat, propertyLng, ready]);

  return (
    <div className="homemap-mapwrap">
      <div ref={containerRef} className="homemap-mapcanvas" />
      {!ready && !failed && (
        <div className="homemap-mapstatus">
          <span>Loading map…</span>
        </div>
      )}
      {failed && (
        <div className="homemap-mapstatus">
          <span>Map unavailable</span>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Slot definitions
// ============================================================================

const ctaLinkDef = {
  label: 'View Neighborhood CTA',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/neighborhood',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'View Neighborhood',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

// ============================================================================
// Component
// ============================================================================

export function HomeMap() {
  const cta = useSlotGroup('homemap.cta', ctaLinkDef);

  // Lazy-load the "Satisfy" cursive Google Font used for the
  // .vibe-cursive-accent span in the heading. De-duped against
  // PropertyOverview / FloorplansList / Header by the data-vibe-font
  // marker so we never load it twice.
  useEffect(() => {
    if (document.querySelector('link[data-vibe-font="satisfy"]')) return;
    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';
    document.head.appendChild(preconnect1);
    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect2);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Satisfy&display=swap';
    link.setAttribute('data-vibe-font', 'satisfy');
    document.head.appendChild(link);
  }, []);

  // Share the same collection as the full NeighborhoodMap section so the
  // editor manages POIs in one place.
  const pois =
    useCollectionBinding<POI>('homemap.pois', { collection: 'neighborhood_pois' }) || [];

  const property = useProperty();
  const propLat = usePropertyField('property_address_latitude');
  const propLng = usePropertyField('property_address_longitude');

  const primaryColor = useMemo(
    () => (property?.property_primary_color as string) || '#0a1f2e',
    [property],
  );

  const lat = parseFloat((propLat as string) || '');
  const lng = parseFloat((propLng as string) || '');
  const hasCoords = !isNaN(lat) && !isNaN(lng);

  return (
    <section className="homemap-section">
      <div className="homemap-inner">
        <div className="homemap-copy">
          <RichTextContent
            path="homemap.heading"
            as="h2"
            className="homemap-heading"
          />

          <RichTextContent path="homemap.body" as="div" className="homemap-body" />

          <div className="homemap-ctas">
            <EditableSlotGroup groupPath="homemap.cta" definition={ctaLinkDef}>
              <a
                className="homemap-cta homemap-cta-primary"
                href={cta.url || '/neighborhood'}
                target={cta._target || '_self'}
              >
                {cta.label || 'View Neighborhood'}
              </a>
            </EditableSlotGroup>
          </div>
        </div>

        <div className="homemap-mapcol">
          {hasCoords ? (
            <MapPreview
              propertyLat={lat}
              propertyLng={lng}
              primaryColor={primaryColor}
              pois={pois}
            />
          ) : (
            <div className="homemap-mapwrap">
              <div className="homemap-mapstatus">
                <span>Map coordinates not available</span>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        /* ============================================================
           Section colors — primary background + text-on-primary copy.
           ============================================================ */
        .homemap-section {
          width: 100%;
          background-color: var(--vibe-color-primary, #0a1f2e);
          color: var(--vibe-color-text-on-primary, #fff);
          padding: 4rem 1.25rem;
        }
        @media (min-width: 768px) {
          .homemap-section {
            padding: 5rem 2rem;
          }
        }
        @media (min-width: 1024px) {
          .homemap-section {
            padding: 6rem 3rem;
          }
        }
        .homemap-inner {
          width: 100%;
          max-width: 1280px;
          margin: 0 auto;
          display: grid;
          /* minmax(0, 1fr) — without the 0 min, the track refuses to
             shrink below its children's min-content (e.g. the 18rem CTA
             pill or the map canvas), which blows out the row on narrow
             mobile viewports. */
          grid-template-columns: minmax(0, 1fr);
          gap: 2.5rem;
          align-items: stretch;
        }
        @media (min-width: 1024px) {
          .homemap-inner {
            grid-template-columns: minmax(0, 1fr) minmax(0, 1.1fr);
            gap: 3.5rem;
          }
        }
        .homemap-copy {
          display: flex;
          flex-direction: column;
          justify-content: center;
          color: var(--vibe-color-text-on-primary, #fff);
        }
        .homemap-heading {
          margin: 0 0 1.5rem 0;
          font-family: var(--vibe-font-heading, 'Prata', serif);
          font-weight: 400;
          line-height: 1.1;
          letter-spacing: -0.01em;
          color: var(--vibe-color-text-on-primary, #fff);
          font-size: 2rem;
        }
        @media (min-width: 768px) {
          .homemap-heading { font-size: 2.75rem; }
        }
        @media (min-width: 1024px) {
          .homemap-heading { font-size: 3.25rem; margin-bottom: 2rem; }
        }

        /* Cursive accent for the heading's <span class="vibe-cursive-accent">
           — Satisfy is lazy-loaded in the useEffect above. Mirrors the same
           accent treatment used in PropertyOverview / AmenitiesGrid /
           FloorplansList headings (rendered in the brand secondary color
           against the Prata serif body of the heading). */
        .vibe-cursive-accent {
          font-family: 'Satisfy', cursive;
          color: var(--vibe-color-secondary);
          font-weight: 400;
          text-transform: none;
          letter-spacing: 0;
        }
        .homemap-body {
          font-family: var(--vibe-font-body, system-ui, sans-serif);
          font-size: 1rem;
          line-height: 1.6;
          color: var(--vibe-color-text-on-primary, #fff);
          margin-bottom: 1rem;
        }
        .homemap-body :is(p, h1, h2, h3) { margin: 0 0 1rem 0; }
        .homemap-body :is(p, h1, h2, h3):last-child { margin-bottom: 0; }
        .homemap-body a {
          color: var(--vibe-color-text-on-primary, #fff);
          text-decoration: underline;
        }
        .homemap-body:last-of-type { margin-bottom: 2rem; }

        /* ============================================================
           CTA — rounded-full pill matching PropertyOverview's primary
           button: filled secondary-color, flips to white on hover.
           ============================================================ */
        .homemap-ctas {
          display: flex;
          gap: 1rem;
          margin-top: 0.5rem;
        }
        .homemap-cta {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 18rem;
          max-width: 100%;
          padding: 0.625rem 1.5rem;
          font-family: var(--vibe-font-body, system-ui, sans-serif);
          font-size: 0.875rem;
          font-weight: 500;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          text-decoration: none;
          border-radius: 9999px;
          border: 1px solid transparent;
          transition: background-color 150ms ease, color 150ms ease,
            border-color 150ms ease;
          cursor: pointer;
        }
        .homemap-cta-primary {
          background-color: var(--vibe-color-secondary, #c9a96b);
          color: var(--vibe-color-text-on-primary, #fff);
          border-color: var(--vibe-color-secondary, #c9a96b);
        }
        .homemap-cta-primary:hover,
        .homemap-cta-primary:focus-visible {
          background-color: #fff;
          color: var(--vibe-color-secondary, #c9a96b);
          border-color: #fff;
        }

        /* ============================================================
           Map column — smaller than the full Neighborhood page map,
           sized to sit beside the copy column.
           ============================================================ */
        .homemap-mapcol {
          display: flex;
          width: 100%;
        }
        .homemap-mapwrap {
          position: relative;
          width: 100%;
          /* The 5px border below would otherwise be added on top of the
             100% width under the default content-box model, making the
             wrap 10px wider than its column and causing horizontal
             scroll on mobile. */
          box-sizing: border-box;
          aspect-ratio: 4 / 3;
          min-height: 280px;
          overflow: hidden;
          border: 5px solid var(--vibe-color-secondary, #c9a96b);
          background-color: rgba(255, 255, 255, 0.08);
        }
        @media (min-width: 1024px) {
          .homemap-mapwrap {
            aspect-ratio: auto;
            height: 100%;
            min-height: 420px;
          }
        }
        .homemap-mapcanvas {
          width: 100%;
          height: 100%;
        }
        .homemap-mapstatus {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: #f5f5f5;
          color: #6b7280;
          font-family: var(--vibe-font-body, system-ui, sans-serif);
          font-size: 0.875rem;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'HomeMap',
  displayName: 'Home Neighborhood Map',
  tokens: {
    'homemap.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue:
        'So <span class="vibe-cursive-accent">much</span> to do in the neighborhood.',
    },
    'homemap.body': {
      label: 'Body Copy',
      type: 'richtext',
      defaultField: 'property_home_neighborhood_blurb',
      defaultValue:
        '<p>With a Walk Score of 99 and a Transit Score of 100, Via6 places you at the heart of one of Seattle’s most interconnected and amenity-rich neighborhoods. Seattle’s major employers are all within easy reach, making your daily commute simple and stress-free. When it’s time to unwind, you’re surrounded by more than 8 million square feet of premium retail within a mile of your front door. You’ll also live at the pulse of Seattle’s cultural scene, making it seamless to catch a Broadway show at the Paramount Theatre, listen to live music at the Showbox, or take in the energy of Lumen Field.</p>',
    },
  },
  slotGroups: {
    'homemap.cta': {
      label: 'View Neighborhood CTA',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultValue: '/neighborhood',
        },
        label: {
          label: 'Link Text',
          type: 'text',
          defaultValue: 'View Neighborhood',
        },
        _target: {
          label: 'Target',
          type: 'text',
          defaultValue: '_self',
        },
      },
    },
  },
  collectionBindings: {
    'homemap.pois': {
      label: 'Points of Interest',
      collection: 'neighborhood_pois',
    },
  },
};

(HomeMap as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default HomeMap;
